import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  extractAcademicProfileFromApiResponse,
  stripAcademicFieldsFromUserPayload,
} from '../utils/academicProfileFromApi';

/** Backend SSO may send `{ user, academicProfile, codingAccess, projectAccess, grantCapabilities, effectiveCapabilities, ... }`; store a flat user + extras. */
function flattenAuthSessionPayload(payload) {
  if (!payload || typeof payload !== 'object') return payload;
  if (payload.user && typeof payload.user === 'object' && payload.user.role != null) {
    return {
      ...payload.user,
      academicProfile: payload.academicProfile,
      codingAccess: payload.codingAccess,
      projectAccess: payload.projectAccess,
      /** Server-resolved year gating for Placement Hub / Projects. */
      featureEligibility: payload.featureEligibility ?? null,
      enrollmentWelcome: payload.enrollmentWelcome ?? null,
      /** Grant-based effective authorization (GATE 3 overlay) */
      grantCapabilities: payload.grantCapabilities ?? [],
      /** Full effective capabilities (defaults + grants combined) for capability-based UI */
      effectiveCapabilities: payload.effectiveCapabilities ?? [],
      assessmentScopes: payload.assessmentScopes ?? [],
      placementScopes: payload.placementScopes ?? [],
      cohortScopes: payload.cohortScopes ?? [],
    };
  }
  return payload;
}

const useAuthStore = create(
  persist(
    (set) => ({
      user: null,
      token: null,
      /** Mirrors `user.role` for guards that select `useAuthStore((s) => s.role)`. */
      role: null,
      /** Full shape `{ rollNumber, joinYear, … }` with nulls; kept off `user`. */
      academicProfile: null,
      /** Grant-based effective authorization capabilities (GATE 3 overlay) */
      grantCapabilities: [],
      /** Full effective capabilities (defaults + grants combined) for capability-based UI */
      effectiveCapabilities: [],
      assessmentScopes: [],
      placementScopes: [],
      cohortScopes: [],
      snackbar: { open: false, message: '', severity: 'info' },

      login: (userData, token) => {
        const flat = flattenAuthSessionPayload(userData);
        const academicProfile = extractAcademicProfileFromApiResponse(
          flat && typeof flat === 'object' ? /** @type {Record<string, unknown>} */ (flat) : null,
          'login'
        );
        const userForStorage = stripAcademicFieldsFromUserPayload(
          flat && typeof flat === 'object' ? /** @type {Record<string, unknown>} */ (flat) : flat
        );
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(userForStorage));
        try {
          sessionStorage.setItem('stacktrack-theme-sync-system', '1');
        } catch {
          /* ignore */
        }
        set({
          user: { ...userForStorage, token },
          token,
          role: userForStorage?.role ?? null,
          academicProfile,
          grantCapabilities: Array.isArray(flat?.grantCapabilities) ? flat.grantCapabilities : [],
          effectiveCapabilities: Array.isArray(flat?.effectiveCapabilities) ? flat.effectiveCapabilities : [],
          assessmentScopes: Array.isArray(flat?.assessmentScopes) ? flat.assessmentScopes : [],
          placementScopes: Array.isArray(flat?.placementScopes) ? flat.placementScopes : [],
          cohortScopes: Array.isArray(flat?.cohortScopes) ? flat.cohortScopes : [],
          snackbar: { open: false, message: '', severity: 'info' },
        });
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        set({
          user: null,
          token: null,
          role: null,
          academicProfile: null,
          grantCapabilities: [],
          effectiveCapabilities: [],
          assessmentScopes: [],
          placementScopes: [],
          cohortScopes: [],
          snackbar: { open: true, message: 'You have successfully logged out.', severity: 'info' },
        });
      },

      closeSnackbar: () =>
        set((s) => ({ snackbar: { ...s.snackbar, open: false } })),
    }),
    {
      name: 'auth-storage',
      partialize: (s) => ({
        user: s.user,
        token: s.token,
        role: s.role ?? s.user?.role ?? null,
        academicProfile: s.academicProfile,
        grantCapabilities: s.grantCapabilities,
        effectiveCapabilities: s.effectiveCapabilities,
        assessmentScopes: s.assessmentScopes,
        placementScopes: s.placementScopes,
        cohortScopes: s.cohortScopes,
      }),
      merge: (persisted, current) => {
        const p = /** @type {Record<string, unknown>} */ (persisted);
        const mergedUser = p?.user;
        const userObj =
          mergedUser && typeof mergedUser === 'object'
            ? /** @type {Record<string, unknown>} */ (mergedUser)
            : null;
        const nestedFromUser =
          userObj?.academicProfile && typeof userObj.academicProfile === 'object'
            ? userObj.academicProfile
            : null;
        const nestedFromPersist =
          p?.academicProfile && typeof p.academicProfile === 'object' ? p.academicProfile : null;
        const flatForAcademic = userObj
          ? {
              ...userObj,
              academicProfile: nestedFromUser || nestedFromPersist || undefined,
            }
          : nestedFromPersist
            ? { academicProfile: nestedFromPersist }
            : null;
        return {
          ...current,
          ...p,
          snackbar: current.snackbar,
          role: p?.role ?? p?.user?.role ?? current.role ?? null,
          academicProfile: extractAcademicProfileFromApiResponse(flatForAcademic, 'persist-rehydrate'),
          grantCapabilities: Array.isArray(p?.grantCapabilities) ? p.grantCapabilities : [],
          effectiveCapabilities: Array.isArray(p?.effectiveCapabilities) ? p.effectiveCapabilities : [],
          assessmentScopes: Array.isArray(p?.assessmentScopes) ? p.assessmentScopes : [],
          placementScopes: Array.isArray(p?.placementScopes) ? p.placementScopes : [],
          cohortScopes: Array.isArray(p?.cohortScopes) ? p.cohortScopes : [],
        };
      },
    }
  )
);

export default useAuthStore;
